import { Typography } from '@neo4j-ndl/react';
import { type FleetManagementTypes, consoleApi, useActiveProject } from '@nx/state';
import { isNonEmptyString } from '@nx/stdlib';

import { hasLicenseIssues } from '../../../../../packages/state/src/slices/fleet-management/types';
import { DeploymentResources } from '../../metrics/deployment-dashboard-metrics';
import { useTokens } from '../hooks/use-tokens';
import { DeploymentStatus } from '../shared/components/deployment-status';
import { UnmonitoredServersBadge, hasUnmonitoredServers } from '../shared/components/server-status';
import { UnknownUser } from '../shared/components/unknown-user';
import { TokenStatus } from '../token-management';
import { DeploymentActionMenu } from './deployment-action-menu';
import { LicenseIssueBadge } from './deployment-inspect-drawer';
import { DeploymentServers } from './deployment-servers';

type DbmsListProps = {
  dbmss: FleetManagementTypes.DbmsDetails[];
  filter: string;
  handleDisplayDbmsDetails: (dbms: FleetManagementTypes.DbmsDetails) => void;
};

export const DbmsList = ({ dbmss, filter, handleDisplayDbmsDetails }: DbmsListProps) => {
  const activeProject = useActiveProject();
  const getProjectMembersRes = consoleApi.useListProjectMembersQuery(activeProject.id);
  const filteredDbmss = dbmss.filter((dbms) => {
    const searchTerm = filter.toLowerCase();
    return dbms.dbmsId.toLowerCase().includes(searchTerm) || dbms.name.toLowerCase().includes(searchTerm);
  });

  const { tokens } = useTokens();

  return (
    <div className="flex flex-col gap-4">
      {filteredDbmss.map((dbms) => {
        const token = tokens.find((t) => t.claimed_by === dbms.dbmsId);
        const creator = getProjectMembersRes.data?.find((member) => member.userId === token?.created_by)?.email;
        return (
          <div
            key={dbms.dbmsId}
            aria-label={`Deployment card for ${dbms.name}`}
            className="border-neutral-border-weak bg-neutral-bg-weak relative shrink-0 grow basis-[136px] rounded-2xl border"
          >
            <div className="mb-3 flex items-start justify-between gap-4 overflow-hidden px-4 pt-4">
              <div className="flex min-w-32 flex-col flex-wrap gap-x-4 gap-y-1 overflow-hidden">
                <Typography variant="h5">{dbms.name}</Typography>
                <DeploymentStatus status={dbms.status} />
                {token && <TokenStatus token={token} />}
                {hasLicenseIssues(dbms) && <LicenseIssueBadge onClick={() => handleDisplayDbmsDetails(dbms)} />}
                {hasUnmonitoredServers(dbms) && <UnmonitoredServersBadge viewType="list" />}
              </div>
              <DeploymentActionMenu dbms={dbms} handleDisplayDbmsDetails={handleDisplayDbmsDetails} />
            </div>

            <div className="flex flex-row justify-between px-4 pb-3">
              <div className="flex flex-row items-center gap-2">
                <Typography variant="body-medium" className="text-neutral-text-weaker">
                  Version: {dbms.version}
                </Typography>
              </div>
            </div>

            <div className="border-neutral-border-weak mx-3 mb-3 flex flex-row justify-between rounded-[8px] border px-3 py-3">
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                <div className="flex flex-row gap-1">
                  <Typography variant="body-small" className="text-neutral-text-weak">
                    Edition:
                  </Typography>
                  <Typography variant="body-small">{dbms.edition}</Typography>
                </div>
                <div className="flex flex-row gap-1">
                  <Typography variant="body-small" className="text-neutral-text-weak">
                    Packaging:
                  </Typography>
                  <Typography variant="body-small">{dbms.packaging}</Typography>
                </div>
                <div className="flex flex-row gap-1">
                  <Typography variant="body-small" className="text-neutral-text-weak">
                    Servers:
                  </Typography>
                  <Typography variant="body-small">
                    {dbms.servers.map((server) => server.address).join(', ')}
                  </Typography>
                </div>
                <div className="flex flex-row gap-1">
                  <Typography variant="body-small" className="text-neutral-text-weak">
                    Registered by:
                  </Typography>
                  <Typography variant="body-small">
                    {isNonEmptyString(creator) ? creator : <UnknownUser userId={token?.created_by} />}
                  </Typography>
                </div>
              </div>
            </div>
            <div className="bg-neutral-bg-on-bg-weak m-3 rounded-[8px] border border-transparent">
              <DeploymentResources dbmsDetails={dbms} projectId={activeProject.id} />
            </div>
            <div className="bg-neutral-bg-on-bg-weak m-3 rounded-[8px] border border-transparent">
              <DeploymentServers dbms={dbms} />
            </div>
          </div>
        );
      })}
    </div>
  );
};
